import React, { useEffect, useRef } from 'react';
import styles from './MicrophoneLevelMeter.module.css';

/**
 * Visualises microphone input as three animated "pill" bars that react to
 * the audio level read from a Web Audio AnalyserNode.
 *
 * ## How it works
 *
 * On every animation frame the component:
 * 1. Reads time-domain audio samples from the AnalyserNode.
 * 2. Computes the RMS (root-mean-square) level and maps it through a
 *    two-segment curve (`rawLevelToDisplay`) so quiet sounds are still
 *    visible while loud sounds don't clip abruptly.
 * 3. Converts the display level to a pixel height for each pill.
 * 4. When input is detected, applies a sine-wave oscillation so the
 *    side pills and centre pill bob in opposite directions, creating a
 *    "breathing" animation.
 *
 * ## Tuning constants
 *
 * Adjust these to change the look and feel of the meter:
 */

/** Pill height (px) when there is no audio input. */
const PILL_MIN_HEIGHT_PX = 3;
/** Pill height (px) at full-scale input. */
const PILL_MAX_HEIGHT_PX = 20;
/** Raw RMS value that maps to the 50 % display mark. Lower values make
 *  quiet sounds appear louder on the meter. */
const DISPLAY_HALF_AT_RAW = 0.1;
/** Exponent applied to the upper half of the display curve. Higher values
 *  compress loud sounds more, giving a gentler ramp to full scale. */
const TOP_SEGMENT_EASE = 2.2;
/** Duration (ms) of one full oscillation cycle. Lower = faster bobbing. */
const PILL_OSCILLATION_PERIOD_MS = 500;
/** Minimum raw RMS before the oscillation kicks in, prevents pills from
 *  wobbling on silence / background noise. */
const INPUT_DETECT_THRESHOLD = 0.012;
/** Amplitude of the oscillation as a fraction of the base height.
 *  0.2 = pills grow/shrink by up to 20 %. */
const PILL_OSCILLATION_STRENGTH = 0.2;
/** Hard ceiling (px) so oscillation can't push pills beyond this height. */
const OSCILLATION_CAP = PILL_MAX_HEIGHT_PX + 6;

/**
 * Maps a raw RMS value (0–1) to a display value (0–1) using a two-segment
 * curve. The lower segment (0 → DISPLAY_HALF_AT_RAW) is linear and maps
 * to the first 50 % of the display range, making quiet sounds clearly
 * visible. The upper segment uses an ease-out power curve controlled by
 * TOP_SEGMENT_EASE so loud levels saturate gradually.
 */
function rawLevelToDisplay(raw: number): number {
  if (raw <= DISPLAY_HALF_AT_RAW) {
    return 0.5 * (raw / DISPLAY_HALF_AT_RAW);
  }
  const t = (raw - DISPLAY_HALF_AT_RAW) / (1 - DISPLAY_HALF_AT_RAW);
  return 0.5 + 0.5 * (1 - (1 - t) ** TOP_SEGMENT_EASE);
}

export interface MicrophoneLevelMeterProps {
  analyserNode: AnalyserNode;
}

export const MicrophoneLevelMeter: React.FC<MicrophoneLevelMeterProps> = ({
  analyserNode,
}) => {
  const leftRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);

  useEffect(() => {
    dataArrayRef.current = new Uint8Array(analyserNode.fftSize);

    const animate = () => {
      const dataArray = dataArrayRef.current;
      if (!dataArray) return;

      analyserNode.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (const byte of dataArray) {
        const sample = (byte - 128) / 128;
        sum += sample * sample;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      const rawLevel = Math.min(rms * 3, 1);

      const displayLevel = rawLevelToDisplay(rawLevel);
      const basePx =
        PILL_MIN_HEIGHT_PX +
        displayLevel * (PILL_MAX_HEIGHT_PX - PILL_MIN_HEIGHT_PX);

      let sidePx = basePx;
      let centerPx = basePx;

      if (rawLevel >= INPUT_DETECT_THRESHOLD) {
        const wave = Math.sin(
          (performance.now() / PILL_OSCILLATION_PERIOD_MS) * 2 * Math.PI,
        );
        sidePx = basePx * (1 + PILL_OSCILLATION_STRENGTH * wave);
        centerPx = basePx * (1 - PILL_OSCILLATION_STRENGTH * wave);

        sidePx = Math.max(
          PILL_MIN_HEIGHT_PX,
          Math.min(sidePx, OSCILLATION_CAP),
        );
        centerPx = Math.max(
          PILL_MIN_HEIGHT_PX,
          Math.min(centerPx, OSCILLATION_CAP),
        );
      }

      if (leftRef.current) leftRef.current.style.height = `${sidePx}px`;
      if (centerRef.current) centerRef.current.style.height = `${centerPx}px`;
      if (rightRef.current) rightRef.current.style.height = `${sidePx}px`;

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [analyserNode]);

  return (
    <div className={styles.strip} data-testid='microphone-level-meter'>
      <div className={styles.pillRow}>
        <div ref={leftRef} className={styles.pill} />
        <div ref={centerRef} className={styles.pill} />
        <div ref={rightRef} className={styles.pill} />
      </div>
    </div>
  );
};
